import { useEffect, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import {
  deleteDealPhoto,
  deleteDealPhotos,
  reorderDealPhotos,
  type DealPhoto,
} from '@/lib/db/admin/deal-photos';
import type { AdminDealStatus } from '@/lib/admin-deal-schemas';
import { getPhotoUrl } from '@/lib/storage/photo-paths';
import {
  IMAGE_MAX_RATIO,
  IMAGE_MIN_COVER_HEIGHT,
  IMAGE_MIN_COVER_WIDTH,
  IMAGE_MIN_GALLERY_HEIGHT,
  IMAGE_MIN_GALLERY_WIDTH,
  IMAGE_MIN_RATIO,
  validateImageDimensions,
} from '@/lib/storage/image-rules';

import './DealPhotoGallery.css';

type BulkScope = 'selected' | 'all';

export type DealPhotoGalleryProps = {
  dealId: string;
  photos: DealPhoto[];
  /**
   * Status of the parent deal. Drives the "Delete all" gating: a deal in
   * `validating` / `published` / `sold` requires ≥ 1 photo (FSM trigger
   * `deals_check_status_transition` in 003_deals.sql:469), so we only
   * allow the global wipe on drafts.
   */
  dealStatus: AdminDealStatus;
  /** Called after a successful reorder/delete so the parent can refetch. */
  onPhotosChange: (photos: DealPhoto[]) => void;
};

export function DealPhotoGallery({
  dealId,
  photos,
  dealStatus,
  onPhotosChange,
}: DealPhotoGalleryProps) {
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<DealPhoto | null>(null);
  const [busyDelete, setBusyDelete] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState<BulkScope | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Drop any selection ids that no longer correspond to a current photo
  // (e.g. after a rollback, refetch, or single delete) so the bulk bar
  // count stays coherent with what is actually visible.
  useEffect(() => {
    const present = new Set(photos.map((p) => p.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (present.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [photos]);

  const canDeleteAll = dealStatus === 'draft';
  const allSelected = photos.length > 0 && selected.size === photos.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = photos.findIndex((p) => p.id === active.id);
    const newIndex = photos.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const optimistic = arrayMove(photos, oldIndex, newIndex).map((p, idx) => ({
      ...p,
      position: idx,
    }));
    const previous = photos;
    onPhotosChange(optimistic);

    try {
      await reorderDealPhotos(
        dealId,
        optimistic.map((p) => p.id),
      );
    } catch {
      onPhotosChange(previous);
      toast.error(t('admin.dealEdit.media.reorderFailedToast'));
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setBusyDelete(true);
    const previous = photos;
    const optimistic = photos
      .filter((p) => p.id !== pendingDelete.id)
      .map((p, idx) => ({ ...p, position: idx }));
    onPhotosChange(optimistic);
    try {
      await deleteDealPhoto({ id: pendingDelete.id, storage_key: pendingDelete.storage_key });
      toast.success(t('admin.dealEdit.media.removedToast'));
      setPendingDelete(null);
    } catch {
      onPhotosChange(previous);
      toast.error(t('admin.dealEdit.media.removeFailedToast'));
    } finally {
      setBusyDelete(false);
    }
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSelectAll() {
    setSelected(new Set(photos.map((p) => p.id)));
  }

  function handleClearSelection() {
    setSelected(new Set());
  }

  async function runBulkDelete(scope: BulkScope) {
    const targets =
      scope === 'selected' ? photos.filter((p) => selected.has(p.id)) : photos.slice();
    if (targets.length === 0) {
      setConfirmBulk(null);
      return;
    }
    setBulkBusy(true);
    const previous = photos;
    const ids = new Set(targets.map((p) => p.id));
    const optimistic = photos
      .filter((p) => !ids.has(p.id))
      .map((p, idx) => ({ ...p, position: idx }));
    onPhotosChange(optimistic);
    try {
      const { ok, failed } = await deleteDealPhotos(
        targets.map((p) => ({ id: p.id, storage_key: p.storage_key })),
      );
      if (ok > 0) {
        toast.success(t('admin.dealEdit.media.deleteSelectedToast', { count: ok }));
      }
      if (failed > 0) {
        // Partial failure: a refetch on the parent will reconcile the true
        // surviving rows. We toast the count so the admin can refresh.
        toast.error(t('admin.dealEdit.media.deleteSelectedPartialError', { count: failed }));
      }
      setSelected(new Set());
      setConfirmBulk(null);
    } catch {
      // Whole-batch network failure — roll back the optimistic UI.
      onPhotosChange(previous);
      toast.error(t('admin.dealEdit.media.deleteSelectedPartialError', { count: targets.length }));
    } finally {
      setBulkBusy(false);
    }
  }

  if (photos.length === 0) {
    return (
      <div className="deal-photo-gallery__empty admin-text-muted">
        {t('admin.dealEdit.media.empty')}
      </div>
    );
  }

  return (
    <>
      <div className="admin-bulk-bar">
        <span className="admin-bulk-bar__count">
          {t('admin.dealEdit.media.selectedCount', { count: selected.size })}
        </span>
        {allSelected ? (
          <button
            type="button"
            className="admin-bulk-btn admin-bulk-btn--ghost"
            disabled={bulkBusy}
            onClick={handleClearSelection}
          >
            {t('admin.dealEdit.media.clearSelection')}
          </button>
        ) : (
          <button
            type="button"
            className="admin-bulk-btn admin-bulk-btn--ghost"
            disabled={bulkBusy}
            onClick={handleSelectAll}
          >
            {t('admin.dealEdit.media.selectAll')}
          </button>
        )}
        {selected.size > 0 ? (
          <button
            type="button"
            className="admin-bulk-btn admin-bulk-btn--danger"
            disabled={bulkBusy}
            onClick={() => setConfirmBulk('selected')}
          >
            {t('admin.dealEdit.media.deleteSelected', { count: selected.size })}
          </button>
        ) : null}
        <button
          type="button"
          className="admin-bulk-btn admin-bulk-btn--danger"
          disabled={bulkBusy || !canDeleteAll}
          title={canDeleteAll ? undefined : t('admin.dealEdit.media.deleteAllDisabledTooltip')}
          onClick={() => setConfirmBulk('all')}
        >
          {t('admin.dealEdit.media.deleteAll')}
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
          <ul className="deal-photo-gallery">
            {photos.map((photo, index) => (
              <SortableItem
                key={photo.id}
                photo={photo}
                isCover={index === 0}
                isSelected={selected.has(photo.id)}
                onSelectToggle={() => toggleOne(photo.id)}
                onDeleteRequest={() => setPendingDelete(photo)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      {pendingDelete && (
        <ConfirmModal
          title={t('admin.dealEdit.media.removeConfirmTitle')}
          body={t('admin.dealEdit.media.removeConfirmBody')}
          confirmLabel={t('admin.dealEdit.media.removeConfirmButton')}
          danger
          onConfirm={() => void confirmDelete()}
          onCancel={() => {
            if (!busyDelete) setPendingDelete(null);
          }}
        />
      )}

      {confirmBulk && (
        <ConfirmModal
          title={
            confirmBulk === 'all'
              ? t('admin.dealEdit.media.deleteAllConfirmTitle', { count: photos.length })
              : t('admin.dealEdit.media.deleteSelectedConfirmTitle', { count: selected.size })
          }
          body={
            confirmBulk === 'all'
              ? t('admin.dealEdit.media.deleteAllConfirmBody', { count: photos.length })
              : t('admin.dealEdit.media.deleteSelectedConfirmBody', { count: selected.size })
          }
          requireType="DELETE"
          danger
          onConfirm={() => {
            if (!bulkBusy) void runBulkDelete(confirmBulk);
          }}
          onCancel={() => {
            if (!bulkBusy) setConfirmBulk(null);
          }}
        />
      )}
    </>
  );
}

type SortableItemProps = {
  photo: DealPhoto;
  isCover: boolean;
  isSelected: boolean;
  onSelectToggle: () => void;
  onDeleteRequest: () => void;
};

/**
 * Compute a dimensions-warning message for the thumb if the photo's stored
 * width/height fall outside the Lot B+A standards (cover ≥ 1600×1200, gallery
 * ≥ 1200×900, ratio 1.0–2.5). Returns null when either dimension is NULL
 * (legacy photo predating Lot B.2/B.3 capture) — we only warn on certain
 * knowledge, not absence of data.
 */
function computeDimsWarning(
  photo: DealPhoto,
  isCover: boolean,
  t: (key: string, params?: Record<string, unknown>) => string,
): string | null {
  if (photo.width_px == null || photo.height_px == null) return null;
  const result = validateImageDimensions(
    photo.width_px,
    photo.height_px,
    isCover ? 'cover' : 'gallery',
  );
  if (result.ok) return null;
  const { code, width, height, ratio } = result.error;
  if (code === 'tooSmallCover') {
    return t('admin.dealEdit.media.dimsWarningTooSmallCover', {
      width,
      height,
      minWidth: IMAGE_MIN_COVER_WIDTH,
      minHeight: IMAGE_MIN_COVER_HEIGHT,
    });
  }
  if (code === 'tooSmallGallery') {
    return t('admin.dealEdit.media.dimsWarningTooSmallGallery', {
      width,
      height,
      minWidth: IMAGE_MIN_GALLERY_WIDTH,
      minHeight: IMAGE_MIN_GALLERY_HEIGHT,
    });
  }
  return t('admin.dealEdit.media.dimsWarningRatio', {
    ratio: ratio.toFixed(2),
    minRatio: IMAGE_MIN_RATIO.toFixed(2),
    maxRatio: IMAGE_MAX_RATIO.toFixed(2),
  });
}

function SortableItem({
  photo,
  isCover,
  isSelected,
  onSelectToggle,
  onDeleteRequest,
}: SortableItemProps) {
  const { t } = useTranslation();
  const sortable = useSortable({ id: photo.id });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };

  const dimsWarning = computeDimsWarning(photo, isCover, t);

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className={`deal-photo-gallery__item${isCover ? ' is-cover' : ''}${isSelected ? ' is-selected' : ''}`}
      data-testid={`gallery-item-${photo.id}`}
    >
      <input
        type="checkbox"
        className="deal-photo-gallery__select"
        checked={isSelected}
        onChange={onSelectToggle}
        // Stop the click from bubbling to the drag-handle button below — the
        // dnd-kit PointerSensor would otherwise start a drag attempt instead
        // of toggling the checkbox.
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        aria-label={t('admin.dealEdit.media.selectPhotoAria', {
          name: photo.alt_text ?? photo.storage_key,
        })}
      />
      <button
        type="button"
        className="deal-photo-gallery__handle"
        aria-label={t('admin.dealEdit.media.dragHandleAria', {
          name: photo.alt_text ?? photo.storage_key,
        })}
        {...sortable.attributes}
        {...sortable.listeners}
      >
        <img
          src={getPhotoUrl(photo)}
          alt={photo.alt_text ?? ''}
          className="deal-photo-gallery__img"
          loading="lazy"
        />
        {isCover && (
          <span className="deal-photo-gallery__cover-badge">
            {t('admin.dealEdit.media.coverBadge')}
          </span>
        )}
        {dimsWarning && (
          <span
            className="deal-photo-gallery__dims-warning"
            role="img"
            aria-label={t('admin.dealEdit.media.dimsWarningAriaLabel')}
            title={dimsWarning}
            data-testid={`gallery-item-warning-${photo.id}`}
          >
            ⚠ {dimsWarning}
          </span>
        )}
      </button>
      <button
        type="button"
        className="deal-photo-gallery__delete"
        onClick={onDeleteRequest}
        aria-label={t('admin.dealEdit.media.removeAria')}
      >
        ×
      </button>
    </li>
  );
}
